import { Routes, Route, Navigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ListeInterventionsPage from "@/pages/maintenance/ListeInterventions";
import DetailInterventionPage from "@/pages/maintenance/DetailIntervention";
import InterventionWizard from "@/pages/maintenance/InterventionWizard";
import ListeArticlesPage from "@/pages/stocks/ListeArticles";
import ListeDemandesAchatPage from "@/pages/da/ListeDemandesAchat";
import CalendrierPage from "@/pages/agenda/Calendrier";
import StatistiquesPage from "@/pages/responsable/Statistiques";
import ListeEnginsPage from "@/pages/engins/ListeEngins";
import DetailEnginPage from "@/pages/engins/DetailEngin";
import UsersPage from "@/pages/admin/Users";
import AuditLogsPage from "@/pages/admin/AuditLogs";
import BackupsPage from "@/pages/admin/Backups";
import ListeContratsPage from "@/pages/contrats/ListeContrats";
import ListeSitesPage from "@/pages/sites/ListeSites";
import ListeFournisseursPage from "@/pages/fournisseurs/ListeFournisseurs";
import ListeFamillesPage from "@/pages/stocks/ListeFamilles";
import ProfilPage from "@/pages/profil/Profil";
import NotificationsPage from "@/pages/profil/Notifications";
import ImportPage from "@/pages/admin/Import";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ToastProvider } from "@/components/ui/toast";
import { ht } from "@/lib/ht";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">{ht("Chargement...")}</div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="maintenance" element={<ListeInterventionsPage />} />
        <Route path="maintenance/nouveau" element={<InterventionWizard mode="create" />} />
        <Route path="maintenance/:id" element={<DetailInterventionPage />} />
        <Route path="maintenance/:id/edit" element={<InterventionWizard mode="edit" />} />
        <Route path="stocks" element={<ListeArticlesPage />} />
        <Route path="engins" element={<ListeEnginsPage />} />
        <Route path="engins/:id" element={<DetailEnginPage />} />
        <Route path="demandes-achat" element={<ListeDemandesAchatPage />} />
        <Route path="agenda-maintenance" element={<CalendrierPage />} />
        <Route path="responsable/statistiques" element={<StatistiquesPage />} />
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/audit-logs" element={<AuditLogsPage />} />
        <Route path="admin/import" element={<ImportPage />} />
        <Route path="admin/backups" element={<BackupsPage />} />
        <Route path="contrats" element={<ListeContratsPage />} />
        <Route path="sites" element={<ListeSitesPage />} />
        <Route path="fournisseurs" element={<ListeFournisseursPage />} />
        <Route path="stocks/familles" element={<ListeFamillesPage />} />
        <Route path="profil" element={<ProfilPage />} />
        <Route path="profil/notifications" element={<NotificationsPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  // AuthProvider wraps everything once at top-level. LoginPage and DashboardPage
  // each create their OWN inner AuthProvider via re-imports to avoid React duplicate
  // context errors — the outer one here handles the ProtectedRoute useAuth.
  // (cf. LoginPage/DashboardPage which embed AuthProvider inside to avoid coupling.)
  return (
    <AuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AuthProvider>
  );
}